// Chakra imports
import { Avatar, Box, Flex, Text, useColorModeValue, Icon, Button, useDisclosure, AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter } from "@chakra-ui/react";
import { useTranslation } from 'react-i18next';
import useAuthentication from "providers/Authentication";
import { CopyIcon, ViewIcon, ViewOffIcon, WarningIcon } from "@chakra-ui/icons";
import Card from "components/card/Card.js";
import React, { useState } from "react";
import constants from "providers/Constants";

export default function Banner(props) {
  const { banner, avatar, name, job, posts, API_KEY } = props;
  const [showFullApiKey, setShowFullApiKey] = useState(false);
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const authentication = useAuthentication();
  const [loading, setLoading] = useState(false);

  // Chakra Color Mode
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = "gray.400";
  const borderColor = useColorModeValue(
    "white !important",
    "#111C44 !important"
  );

  // Function to mask API key
  const maskApiKey = (apiKey) => {
    if (!apiKey || apiKey.length < 8) return apiKey;
    const visibleChars = 4;
    const start = apiKey.substring(0, visibleChars);
    const end = apiKey.substring(apiKey.length - visibleChars);
    const masked = '*'.repeat(apiKey.length - (visibleChars * 2));
    return `${start}${masked}${end}`;
  };

  const displayedApiKey = showFullApiKey ? API_KEY : maskApiKey(API_KEY);
  return (
    <Card mb={{ base: "0px", lg: "0px" }} align='center'>
      <Box
        bg={`url(${banner})`}
        bgSize='cover'
        borderRadius='16px'
        h='131px'
        w='100%'
      />
      <Avatar
        mx='auto'
        src={avatar}
        h='87px'
        w='87px'
        mt='-43px'
        border='4px solid'
        borderColor={borderColor}
      />
      <Text color={textColorPrimary} fontWeight='bold' fontSize='xl' mt='10px'>
        {name}
      </Text>
      <Text color={textColorSecondary} fontSize='sm'>
        {job}
      </Text>
      <Flex w='100%' mx='auto' mt='26px' px={{ base: '12px', md: '0px' }}>
        <Flex mx='auto' align='center' direction='column' w={{ base: '100%', md: 'max-content' }}>
          <Flex align='center' gap='8px' flexWrap='wrap' justify='center' w='100%'>
            <Text color={textColorPrimary} fontSize={{ base: 'md', md: '2xl' }} fontWeight='700' wordBreak='break-all'>
              {displayedApiKey}
            </Text>
            <Icon
              onClick={() => setShowFullApiKey(!showFullApiKey)}
              as={showFullApiKey ? ViewOffIcon : ViewIcon}
              color='gray.400'
              cursor='pointer'
              flexShrink={0}
              _hover={{ color: 'gray.600' }}
            />
            <Icon
              onClick={() => {
                navigator.clipboard.writeText(API_KEY);
                constants.showToast('success', t('SUCCESS'), t('API Key copied to clipboard'));
              }}
              as={CopyIcon}
              color='gray.400'
              cursor='pointer'
              flexShrink={0}
              _hover={{ color: 'gray.600' }}
            />
          </Flex>
          <Flex mt={2} align='center' gap={4} direction={{ base: 'column', md: 'row' }}>
            <Text color={textColorSecondary} fontSize='sm' fontWeight='400'>
              {t('API Key')}
            </Text>
            <Button
              size='xs'
              colorScheme='red'
              variant='solid'
              leftIcon={<WarningIcon />}
              onClick={onOpen}
              aria-label={t('Regenerate API Key')}
            >
              {t('Regenerate API Key')}
            </Button>
          </Flex>
        </Flex>
      </Flex>

      {/* Regenerate confirmation dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {t('Regenerate API Key')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('Regenerate API Key confirmation')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} mr={3}>
                {t('Cancel')}
              </Button>
              <Button colorScheme='red' isLoading={loading} disabled={loading} onClick={() => { refreshApiKey(API_KEY); }}>
                {t('Proceed')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Card>
  );

  // Regenerate API Key 
  function refreshApiKey(API_KEY) {
    setLoading(true);
    authentication.refreshApiKey({ identifier: 'regenerate_api', api_key: API_KEY }, (error, response) => {
      setLoading(false);
      if (error) {
        constants.showToast(
          "error",
          t('FAILED'),
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.storeData('API_KEY', response?.api_key);
        constants.storeData('EMAIL', response?.email);
        constants.storeData('AUTH_TOKEN', response?.basic_token);
        constants.showToast('success', t('SUCCESS'), t('API Key regenerated'));
        onClose();
        window.location.reload();
      }
    });
  }
}
